import { z } from 'zod';
import { STAGE_FEEDBACK_SURFACES } from './StageFeedback';

export const SIXYNC_TELESTRATION_INK_COLORS = ['yellow', 'red'] as const;
export const SIXYNC_TELESTRATION_MAX_POINTS = 512;
export const SIXYNC_TELESTRATION_DEFAULT_WIDTH_NORM = 0.01;
export const TELESTRATION_MAX_STROKES = 64;
export const TELESTRATION_MAX_RETIRED_IDS = 256;
export const TELESTRATION_MAX_STATE_BYTES = 1024 * 1024;
export const TELESTRATION_COLORS = { yellow: '#FFD400', red: '#FF2D2D' } as const;
export const SixyncTelestrationInkColorSchema = z.enum(SIXYNC_TELESTRATION_INK_COLORS);
/**
 * What a stroke's normalized points are relative to. `stage` (also when absent, as every
 * stroke captured before this field was): the centred 16:9 stage rectangle. `picture`: the
 * shared stage media's own displayed picture (`SharedMediaInkOverlay`, captured with
 * `fill`). The surface alone cannot say which: Townhall captures `shared_stage_media` ink
 * in stage space, the standard room in picture space. Renderers that draw the whole stage
 * (the program canvas, the compositor) map picture-space ink onto the media layer's picture.
 */
export const TELESTRATION_INK_SPACES = ['stage', 'picture'] as const;
export const TelestrationInkSpaceSchema = z.enum(TELESTRATION_INK_SPACES);
/**
 * Which picture a picture-space stroke belongs to when it is not the shared stage media.
 * `share`: the desktop share on stage, named by its desktop AV source id, so the stroke
 * lands on the share's displayed picture on every surface (#5057). Shaped like the
 * `share` anchor of #4488 so that model can absorb it. Absent: stage media, as before.
 */
export const TELESTRATION_ANCHOR_SOURCE_ID = /^[A-Za-z0-9{}:._-]{1,128}$/;
export const TelestrationShareAnchorSchema = z
  .object({ kind: z.literal('share'), sourceId: z.string().regex(TELESTRATION_ANCHOR_SOURCE_ID) })
  .strict();
export const SixyncTelestrationPointSchema = z.object({
  x: z.number().finite().min(0).max(1),
  y: z.number().finite().min(0).max(1),
});
export const TelestrationBatchSchema = z.object({
  strokeId: z.string().uuid(),
  inkGeneration: z.number().int().nonnegative().safe(),
  color: SixyncTelestrationInkColorSchema,
  widthNorm: z.number().finite().min(0.004).max(0.03),
  points: z.array(SixyncTelestrationPointSchema).min(1).max(SIXYNC_TELESTRATION_MAX_POINTS),
  complete: z.boolean(),
  batchSeq: z.number().int().nonnegative().safe(),
  surface: z.enum(STAGE_FEEDBACK_SURFACES).default('stage'),
  space: TelestrationInkSpaceSchema.optional(),
  /** Only with `space: 'picture'`; the service rejects any other combination. */
  anchor: TelestrationShareAnchorSchema.optional(),
});
export const SixyncTelestrationStrokePayloadSchema = TelestrationBatchSchema.extend({
  inkRevision: z.number().int().nonnegative().safe(),
  drawnByActorId: z.string().min(1).max(128),
});
export const TelestrationStoredStrokeSchema = SixyncTelestrationStrokePayloadSchema.extend({
  cueId: z.string().min(1).max(160),
  startedAt: z.number().finite().nonnegative(),
});
export const TelestrationStateSchema = z.object({
  roomId: z.string().min(1),
  inkGeneration: z.number().int().nonnegative().safe(),
  inkRevision: z.number().int().nonnegative().safe(),
  strokes: z.array(TelestrationStoredStrokeSchema).max(TELESTRATION_MAX_STROKES),
});
export type SixyncTelestrationInkColor = z.infer<typeof SixyncTelestrationInkColorSchema>;
export type SixyncTelestrationPoint = z.infer<typeof SixyncTelestrationPointSchema>;
export type TelestrationInkSpace = z.infer<typeof TelestrationInkSpaceSchema>;
export type TelestrationShareAnchor = z.infer<typeof TelestrationShareAnchorSchema>;
export type SixyncTelestrationStrokePayload = z.infer<typeof SixyncTelestrationStrokePayloadSchema>;
export type TelestrationBatch = z.infer<typeof TelestrationBatchSchema>;
export type TelestrationStoredStroke = z.infer<typeof TelestrationStoredStrokeSchema>;
export type TelestrationState = z.infer<typeof TelestrationStateSchema>;
export type TelestrationRemovalReason = 'undo' | 'clear' | 'surface_changed' | 'reconcile';
export interface TelestrationRemoved {
  roomId: string;
  cueIds: string[];
  reason: TelestrationRemovalReason;
  inkGeneration: number;
  inkRevision: number;
}
